/**
 * SplitPanelHeader
 *
 * Header component for split view panels.
 * Displays the panel title and action buttons (close, maximize).
 * Supports drag & drop for reordering panels.
 * Right-click shows context menu with additional options.
 */

import React, { useState, useRef, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaExpand,
  FaHome,
  FaChartBar,
  FaRss,
  FaBell,
  FaFlask,
  FaComments,
  FaSearch,
  FaCog,
  FaTh,
  FaGripVertical,
} from "react-icons/fa";
import { SplitPanel, SplitPanelType } from "@/hooks/use-tabs-store";
import { useSplitView } from "./SplitViewProvider";
import SplitPanelContextMenu from "./SplitPanelContextMenu";

// ============================================================================
// Types
// ============================================================================

interface SplitPanelHeaderProps {
  panel: SplitPanel;
  isFocused?: boolean;
  onClose?: () => void;
  onFocus?: () => void;
  onDragStateChange?: (isDragging: boolean) => void;
  panelRef?: React.RefObject<HTMLDivElement | null>;
  /** Callback when "Open in new tab" is clicked from context menu */
  onOpenInNewTab?: () => void;
  /** Callback when "Open in new window" is clicked from context menu */
  onOpenInNewWindow?: () => void;
  /** Callback when panel should be moved to a tab */
  onMovePanelToTab?: (tabId: string | "new") => void;
}

// ============================================================================
// Helper: Get icon for panel type
// ============================================================================

function getIconForType(type: SplitPanelType): React.ReactNode {
  switch (type) {
    case "dashboard":
      return <FaChartBar size={10} />;
    case "tracker":
    case "trackers":
      return <FaRss size={10} />;
    case "alert":
    case "alerts":
      return <FaBell size={10} />;
    case "enrichment":
    case "enrichments":
      return <FaFlask size={10} />;
    case "chat":
      return <FaComments size={10} />;
    case "search":
      return <FaSearch size={10} />;
    case "home":
      return <FaHome size={10} />;
    case "account":
      return <FaCog size={10} />;
    case "widgets":
      return <FaTh size={10} />;
    default:
      return null;
  }
}

// ============================================================================
// Component
// ============================================================================

const SplitPanelHeader: React.FC<SplitPanelHeaderProps> = ({
  panel,
  isFocused = false,
  onClose,
  onFocus,
  onDragStateChange,
  panelRef,
  onOpenInNewTab,
  onOpenInNewWindow,
  onMovePanelToTab,
}) => {
  const { closePanel, focusPanel, exitSplitView, panelCount, swapPanels } = useSplitView();
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const dragCounterRef = useRef(0);

  // Context menu handlers
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleOpenInNewTab = useCallback(() => {
    if (onOpenInNewTab) {
      onOpenInNewTab();
    } else if (panel.route) {
      // Default behavior: open in new browser tab
      window.open(panel.route, "_blank");
    }
  }, [onOpenInNewTab, panel.route]);

  const handleOpenInNewWindow = useCallback(() => {
    if (onOpenInNewWindow) {
      onOpenInNewWindow();
    } else if (panel.route) {
      // Default behavior: open in new browser window
      window.open(
        panel.route,
        "_blank",
        "width=1200,height=800,menubar=no,toolbar=no,location=no,status=no"
      );
    }
  }, [onOpenInNewWindow, panel.route]);

  const handleMovePanelToTab = useCallback(
    (tabId: string | "new") => {
      if (onMovePanelToTab) {
        onMovePanelToTab(tabId);
      }
    },
    [onMovePanelToTab]
  );

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClose) {
      onClose();
    } else {
      closePanel(panel.id);
    }
  };

  const handleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation();
    focusPanel(panel.id);
    exitSplitView();
  };

  const handleFocus = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFocus) {
      onFocus();
    } else {
      focusPanel(panel.id);
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    onDragStateChange?.(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", panel.id);
    e.dataTransfer.setData("application/x-split-panel", panel.id);

    // Use the entire panel as drag preview
    if (panelRef?.current) {
      const rect = panelRef.current.getBoundingClientRect();
      // Scale down to a small preview (max 150px)
      const maxSize = 150;
      const scale = Math.min(1, maxSize / Math.max(rect.width, rect.height));
      const scaledWidth = rect.width * scale;
      const scaledHeight = rect.height * scale;
      
      // Create a wrapper that has the final scaled size
      const wrapper = document.createElement("div");
      wrapper.style.position = "absolute";
      wrapper.style.top = "-9999px";
      wrapper.style.left = "-9999px";
      wrapper.style.width = `${scaledWidth}px`;
      wrapper.style.height = `${scaledHeight}px`;
      wrapper.style.overflow = "hidden";
      wrapper.style.borderRadius = "6px";
      wrapper.style.boxShadow = "0 8px 24px rgba(0,0,0,0.25)";
      
      // Create a scaled clone inside the wrapper
      const clone = panelRef.current.cloneNode(true) as HTMLElement;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.transform = `scale(${scale})`;
      clone.style.transformOrigin = "top left";
      clone.style.opacity = "0.9";
      
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);
      
      // Set the drag image with offset to center it
      e.dataTransfer.setDragImage(wrapper, scaledWidth / 2, scaledHeight / 2);
      
      // Remove the wrapper after a short delay
      requestAnimationFrame(() => {
        document.body.removeChild(wrapper);
      });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragStateChange?.(false);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes("application/x-split-panel")) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes("application/x-split-panel")) {
      e.dataTransfer.dropEffect = "move";
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);

    const sourcePanelId = e.dataTransfer.getData("application/x-split-panel");
    if (sourcePanelId && sourcePanelId !== panel.id) {
      swapPanels(sourcePanelId, panel.id);
    }
  };

  return (
    <>
      <div
        className={`
          flex items-center justify-between px-3 py-1.5
          bg-surface-elevated border-b-2
          ${isFocused ? "border-b-accent" : "border-b-arc-border"}
          ${isDragging ? "opacity-50" : ""}
          ${isDragOver ? "bg-accent/10 border-b-accent" : ""}
          transition-all duration-200
        `}
        onClick={handleFocus}
        onContextMenu={handleContextMenu}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
      {/* Left side - Panel info */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Drag handle */}
        {panelCount > 1 && (
          <div className="flex-shrink-0 cursor-grab active:cursor-grabbing text-content-tertiary hover:text-content-secondary">
            <FaGripVertical size={10} />
          </div>
        )}

        {/* Panel type icon */}
        <div
          className={`
            flex-shrink-0 p-1 rounded
            ${isFocused ? "text-accent bg-accent/10" : "text-content-tertiary"}
          `}
        >
          {getIconForType(panel.type)}
        </div>

        {/* Panel title */}
        <span className="text-xs font-medium text-content-primary truncate max-w-[150px]">
          {panel.title || panel.type}
        </span>

        {/* Focus indicator dot */}
        {isFocused && (
          <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
        )}
      </div>

      {/* Right side - Action buttons */}
      <div className="flex items-center gap-0.5 h-6">
        {/* Maximize button */}
        <button
          onClick={handleMaximize}
          className="p-1 rounded text-content-tertiary hover:text-emerald-600 hover:bg-surface-tertiary transition-colors"
          title="Maximiser ce panneau"
        >
          <FaExpand size={10} />
        </button>

        {/* Close button - only show if more than 1 panel */}
        {panelCount > 1 && (
          <button
            onClick={handleClose}
            className="p-1 rounded text-content-tertiary hover:text-red-600 hover:bg-surface-tertiary transition-colors"
            title="Fermer ce panneau"
          >
            <FaTimes size={10} />
          </button>
        )}
      </div>
      </div>

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <SplitPanelContextMenu
            position={contextMenu}
            panel={panel}
            onClose={closeContextMenu}
            onOpenInNewTab={handleOpenInNewTab}
            onOpenInNewWindow={handleOpenInNewWindow}
            onMovePanelToTab={handleMovePanelToTab}
          />
        )}
      </AnimatePresence>
    </>
  );
};

SplitPanelHeader.displayName = "SplitPanelHeader";

export default SplitPanelHeader;
